function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { Segmentation } from 'common/types/reports/query';
// eslint-disable-next-line import/no-cycle -- string bucket pane renders qes; this will go away when we remove the old string buckets
import { StringBucketPaneReact } from 'common/widgets/bucket-pane/string-bucket-pane';
import { SvgIconReact } from 'common/components/svg-icon';
import { InputReact } from 'common/components/input';
import { InputChangeEvent, InputSize } from 'common/components/input/types';
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme, ButtonType } from 'common/components/button/constants';
import { StringBucketType, TextAnalysisStringBucket, TextAnalysisStringGroup } from 'common/widgets/bucket-pane/types';
import { appApiUrl } from 'common/report/queries/utils';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { ApiResponse } from 'common/util/shared-client';
import { EventDefinition } from 'common/util/definitions-types';
import { queryClient } from 'common/react-query/query-client';
import { SkeletonListReact } from 'common/components/skeleton-list';
import { SkeletonListRowSize } from 'common/components/skeleton-list/types';
import { useTranslation } from 'common/i18n/hooks';
import { Prompter } from '../prompter';
import styles from '../index.module.styl';
export function StringBuckets(props: {
  eventNames: string[];
  textAnalysisEvent?: EventDefinition;
  isTextAnalysis?: boolean;
  segment: Segmentation;
  listProperty?: Segmentation;
  onUpdate: (segment: Segmentation, isValid: boolean) => void;
}) {
  return props.isTextAnalysis ? <TextAnalysisStringBuckets textAnalysisEvent={props.textAnalysisEvent} segment={props.segment} onUpdate={props.onUpdate} data-sentry-element="TextAnalysisStringBuckets" data-sentry-component="StringBuckets" data-sentry-source-file="string.tsx" /> : <StandardStringBuckets eventNames={props.eventNames} segment={props.segment} listProperty={props.listProperty} onUpdate={props.onUpdate} data-sentry-element="StandardStringBuckets" data-sentry-component="StringBuckets" data-sentry-source-file="string.tsx" />;
}
function StandardStringBuckets(props: {
  eventNames: string[];
  segment: Segmentation;
  listProperty?: Segmentation;
  onUpdate: (segment: Segmentation, isValid: boolean) => void;
}) {
  // reusing old string buckets for now, but we will replace this UX
  return <StringBucketPaneReact className={styles.fullWidth} eventNames={props.eventNames} onUpdate={_ref => {
    let {
      isValid,
      segment
    } = _ref;
    return props.onUpdate(segment, isValid);
  }} segment={props.segment} listProperty={props.listProperty} data-sentry-element="StringBucketPaneReact" data-sentry-component="StandardStringBuckets" data-sentry-source-file="string.tsx" />;
}
function TextAnalysisStringBuckets(props: {
  className?: string;
  textAnalysisEvent?: EventDefinition;
  segment: Segmentation;
  onUpdate: (segment: Segmentation, isValid: boolean) => void;
}) {
  var _categorize$data;
  const {
    t
  } = useTranslation();
  const project = useCurrentProject();
  const update = (bucket: TextAnalysisStringBucket) => {
    props.onUpdate(_objectSpread(_objectSpread({}, props.segment), {}, {
      customGroups: bucket
    }), areTextAnalysisGroupsValid(bucket));
  };

  // even though this is more like a query, using a mutation since it's quite expensive and we don't want any autofetch or caching behavior at this time
  const categorize = useMutation({
    mutationFn: async (prompt: string) => {
      var _project$get, _props$textAnalysisEv;
      const url = appApiUrl("ai/categorize", {
        projectId: (_project$get = project.get()) === null || _project$get === void 0 || (_project$get = _project$get.value) === null || _project$get === void 0 ? void 0 : _project$get.id
      });
      const res = await fetch(url, {
        method: "POST",
        body: JSON.stringify({
          prompt,
          eventName: (_props$textAnalysisEv = props.textAnalysisEvent) === null || _props$textAnalysisEv === void 0 ? void 0 : _props$textAnalysisEv.name,
          textProperty: props.segment.propertyName
        }),
        headers: {
          'Content-Type': "application/json"
        }
      });
      const json = await res.json();
      return json as ApiResponse<{
        name: string;
        responseText: string;
        categories: Array<{
          name: string;
          description: string;
        }>;
      }>;
    },
    onSuccess: data => {
      if (data.status === "ok") {
        const bucket: TextAnalysisStringBucket = {
          type: StringBucketType.TextAnalysis,
          name: data.results.name,
          groups: data.results.categories
        };
        update(bucket);
      } else {
        console.error("Failed to categorize:", data.error);
      }
    }
  }, queryClient);
  const promptResponseText = ((_categorize$data = categorize.data) === null || _categorize$data === void 0 ? void 0 : _categorize$data.status) === "ok" ? categorize.data.results.responseText : "";
  const currentGroups: TextAnalysisStringBucket = props.segment.customGroups && props.segment.customGroups.type === StringBucketType.TextAnalysis ? props.segment.customGroups : {
    type: StringBucketType.TextAnalysis,
    name: "",
    groups: []
  };
  const onPrompterSubmit = (prompt: string) => {
    categorize.mutate(prompt);
  };
  const onClickAdd = () => {
    update(_objectSpread(_objectSpread({}, currentGroups), {}, {
      groups: [...currentGroups.groups, {
        name: "",
        description: ""
      }]
    }));
  };
  const onClickDelete = (index: number) => {
    const groups = [...currentGroups.groups];
    groups.splice(index, 1);
    update(_objectSpread(_objectSpread({}, currentGroups), {}, {
      groups
    }));
  };
  const onChangeName = (name: string) => {
    update(_objectSpread(_objectSpread({}, currentGroups), {}, {
      name
    }));
  };
  const onChangeGroup = (index: number, updatedGroup: TextAnalysisStringGroup) => {
    const groups = [...currentGroups.groups];
    groups[index] = updatedGroup;
    update(_objectSpread(_objectSpread({}, currentGroups), {}, {
      groups
    }));
  };
  return <>
      <div className={styles.stringBuckets}>
        <InputReact className={styles.groupsName} label='Group Name' value={currentGroups.name} size={InputSize.Medium} disabled={categorize.isPending} onChange={ev => onChangeName((ev as InputChangeEvent).detail.value)} data-sentry-element="InputReact" data-sentry-source-file="string.tsx" />
        <div className={styles.groupsHeader}>
          <SvgIconReact icon='custom-segments' data-sentry-element="SvgIconReact" data-sentry-source-file="string.tsx" />
          <span>{t("query-builder.buckets.groups")}</span>
        </div>
        {categorize.isPending ? <SkeletonListReact rowSize={SkeletonListRowSize.Header} /> : <>
            {currentGroups.groups.map((group, index) => <TextAnalysisGroup key={index} group={group} onUpdate={updatedGroup => onChangeGroup(index, updatedGroup)} onClickDelete={() => onClickDelete(index)} />)}
            <ButtonReact className={styles.addGroupButton} icon='plus' theme={ButtonTheme.Tertiary} onClick={onClickAdd}>
              {t("query-builder.buckets.add-group")}
            </ButtonReact>
          </>}
      </div>
      <Prompter loading={categorize.isPending} responseText={promptResponseText} onSubmit={onPrompterSubmit} data-sentry-element="Prompter" data-sentry-source-file="string.tsx" />
    </>;
}
function TextAnalysisGroup(props: {
  group: TextAnalysisStringGroup;
  onUpdate: (group: TextAnalysisStringGroup) => void;
  onClickDelete: () => void;
}) {
  const {
    t
  } = useTranslation();
  return <div className={styles.stringGroup} data-sentry-component="TextAnalysisGroup" data-sentry-source-file="string.tsx">
      <div className={styles.stringGroupTop}>
        <input type='text' className={clsx(styles.groupInput, styles.stringGroupName)} value={props.group.name} autoFocus={!props.group.name} placeholder={t("query-builder.buckets.untitled-group")} onChange={ev => props.onUpdate(_objectSpread(_objectSpread({}, props.group), {}, {
        name: ev.target.value
      }))} />
        <ButtonReact className={styles.groupAction} icon='x' type={ButtonType.Destructive} size={ButtonSize.Small} theme={ButtonTheme.Tertiary} onClick={props.onClickDelete} data-sentry-element="ButtonReact" data-sentry-source-file="string.tsx" />
      </div>
      <textarea className={clsx(styles.groupInput, styles.stringGroupDescription)} value={props.group.description} placeholder={t("query-builder.buckets.no-description")} onChange={ev => props.onUpdate(_objectSpread(_objectSpread({}, props.group), {}, {
      description: ev.target.value
    }))} />
    </div>;
}
function areTextAnalysisGroupsValid(bucket: TextAnalysisStringBucket): boolean {
  // must have a name
  if (!bucket.name.trim()) {
    return false;
  }

  // must have at least 2 valid groups
  const validGroups = bucket.groups.filter(isTextAnalysisGroupValid);
  return validGroups.length >= 2;
}
function isTextAnalysisGroupValid(group: TextAnalysisStringGroup): boolean {
  // must have a name
  return !!group.name.trim();
}